import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { getGuideAccent } from "../../utils/guideAccent";

// Template317 (Edimburgo): Qué comer
const Template317 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const introText = page.texts?.[1]?.content;

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const sections = Array.isArray(page?.sections) ? page.sections : [];

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "4px",
		color: accent.accent,
		marginTop: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 12,
		color: contentColor,
		margin: 0,
		lineHeight: 1.35,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box
					data-block-id="text-1"
					sx={{ padding: "10px 16px 6px", marginBottom: 1 }}
				>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{
					padding: "0 16px 16px",
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					position: "relative",
				}}
			>
				{sections.map((section, idx) => {
					const stickerSrc = getCanvasImageUrl(section.stickerCanvasId);
					const stickerOnRight = section.stickerSide !== "left";
					const isHaggis = section.stickerCanvasId === "haggis-sticker";
					const isOink = section.stickerCanvasId === "oink-food-sticker";
					const isRightBox = section.align === "right";
					const isHighlight = section.layout === "highlight";

					const stickerBlock = stickerSrc ? (
						<Box
							sx={{
								position: "relative",
								width: isSmallScreen ? 168 : 188,
								flexShrink: 0,
								minHeight: 88,
								overflow: "visible",
							}}
						>
							<img
								src={stickerSrc}
								alt=""
								style={{
									width: 88,
									position: "absolute",
									...(isHaggis
										? {
												right: isSmallScreen ? -8 : -8,
												bottom: isSmallScreen ? -30 : -6,
											}
										: isOink
											? {
													left: isSmallScreen ? -30 : -8,
													bottom: isSmallScreen ? -20 : -6,
												}
											: {
													[stickerOnRight ? "right" : "left"]: -8,
													bottom: isSmallScreen ? -20 : -6,
												}),
									zIndex: 5,
								}}
							/>
							{section.bubble && (
								<Box
									sx={{
										position: "absolute",
										...(stickerOnRight
											? {
													right: isSmallScreen ? 42 : 72,
													top: isSmallScreen ? -20 : 8,
												}
											: {
													left: isSmallScreen ? 40 : 60,
													top: isSmallScreen ? -10 : 8,
												}),
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: stickerOnRight
											? "16px 16px 0px 16px"
											: "16px 16px 16px 0px",
										padding: "7px 9px",
										fontSize: 11,
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										width: isSmallScreen ? 118 : 128,
										zIndex: 6,
										lineHeight: 1.35,
									}}
								>
									{section.bubble}
								</Box>
							)}
						</Box>
					) : null;

					return (
						<Box
							key={idx}
							data-block-id={`text-${idx + 2}`}
							sx={{
								display: "flex",
								flexDirection: stickerSrc
									? stickerOnRight
										? "row"
										: "row-reverse"
									: "row",
								alignItems: "center",
								gap: stickerSrc ? "8px" : 0,
							}}
						>
							<Box
								sx={{
									backgroundColor: isHighlight ? "#f7fafc" : accent.boxBg,
									border: isHighlight
										? `2px solid ${accent.boxBorder}`
										: "none",
									borderRadius: "12px",
									padding: "8px 10px",
									width: stickerSrc ? "62%" : isRightBox ? "80%" : "100%",
									marginLeft: !stickerSrc && isRightBox ? "auto" : 0,
									flex: stickerSrc ? "1 1 auto" : undefined,
									minWidth: 0,
									boxSizing: "border-box",
								}}
							>
								{section.title && (
									<Box component="h3" sx={h3}>
										{section.title}
									</Box>
								)}
								{section.body && (
									<Box component="p" sx={p}>
										{section.body}
									</Box>
								)}
							</Box>
							{stickerBlock}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

Template317.thumbnail = "/images/template-1.png";

export default Template317;
